import { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./forms.css";

import { APP_GTAG_CONVERSION_ID } from "../../utils/env";
import { useScroll } from "../../context/scroll-context";
import { useAcubeService } from "../../service/acube-api.hook";

const JoinUsForm = () => {
  const [formValues, setFormValues] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { postProxy } = useAcubeService();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    clearError(name);
  };

  const handlePhoneChange = (value) => {
    setFormValues({ ...formValues, phone: value });
    clearError("phone");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      clearError("file");
    } else {
      setFile(null);
      setErrors((prevErrors) => ({
        ...prevErrors,
        file: "Only PDF files are allowed",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formValues.name) newErrors.name = "Name is required";
    if (!formValues.email) newErrors.email = "Email is required";
    if (!formValues.phone) newErrors.phone = "Phone number is required";
    if (!file) newErrors.file = "Please upload a PDF file";
    // Email validation using regex
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formValues.email && !emailPattern.test(formValues.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmail = async () => {
    const formData = new FormData();
    formData.append("name", formValues.name);
    formData.append("email", formValues.email);
    formData.append("phone", formValues.phone);
    formData.append("message", formValues.message);
    formData.append("type", "joinus");
    formData.append("file", file);

    await postProxy("contact/join-us", formData)
      .then(() => {
        setSuccessMessage(
          "Thank you for reaching out! Your message has been sent successfully."
        );
        setErrorMessage("");
        setIsLoading(false);
        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: APP_GTAG_CONVERSION_ID + "/Mw06CIGEnIwYEOeYrKQp",
            event_category: "User",
            event_action: "Form Submission",
            event_label: "Form Button Click",
          });
        }
        setTimeout(() => {
          setSuccessMessage("");
          setFormValues({
            name: "",
            company: "",
            email: "",
            phone: "",
            message: "",
          });
          setFile("");
        }, 5000);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("There was an error sending the email:", error);
        setSuccessMessage("");
        setErrorMessage(
          "Oops! Something went wrong while sending your message. Please try again later."
        );
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      sendEmail();
    } else {
      setIsLoading(false);
      setSuccessMessage("");
      setErrorMessage(
        "Please make sure all required fields are filled out correctly before submitting the form."
      );
    }
  };
  const clearError = (field) => {
    // Remove the specific error field
    if (errors[field]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors, [field]: null };
        // Check if there are any remaining errors after clearing the current one
        const hasRemainingErrors = Object.values(newErrors).some(
          (error) => error !== null
        );
        if (!hasRemainingErrors) {
          setErrorMessage("");
        }
        return newErrors;
      });
    }
  };

  const { contactform } = useScroll();
  return (
    <section className="contact-form-section py-5" id="section1">
      <Container fluid>
        <h2
          className="text-center mb-4 text-primary ft-wt-600 text-uppercase"
          ref={contactform}
        >
          Join Our Team
        </h2>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        <Form onSubmit={handleSubmit} id="footer-contact-form" noValidate>
          <Row className="mb-30 gap-4 gap-lg-0">
            <Col xs="12" lg="4">
              <Form.Group controlId="formName" className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Full Name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs="12" lg="4">
              <Form.Group controlId="formEmail" className="position-relative">
                <Form.Control
                  type="email"
                  placeholder="Email Address"
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs="12" lg="4">
              <Form.Group controlId="formPhone" className="position-relative">
                <PhoneInput
                  country={"in"}
                  inputProps={{
                    name: "phone",
                    required: true,
                    autoFocus: false,
                  }}
                  placeholder="Mobile Number*"
                  value={formValues.phone}
                  onChange={handlePhoneChange}
                  isInvalid={!!errors.phone}
                />
                {errors.phone && (
                  <div className="text-danger position-absolute d-block ft-12 phone-error">
                    {errors.phone}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 gap-4 gap-lg-0">
            <Col xs="12" lg="4">
              <Form.Group controlId="formFile" className="position-relative">
                <Form.Control
                  type="file"
                  name="file"
                  onChange={handleFileChange}
                  isInvalid={!!errors.file}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.file}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs="12" lg="4">
              <Form.Group controlId="formMessage">
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Share Your Message"
                  name="message"
                  value={formValues.message}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="text-center d-flex flex-column gap-4 align-items-center">
            <Button
              variant="primary"
              type="submit"
              className="text-uppercase gap-2 d-flex align-items-center"
            >
              {isLoading && <Spinner size="sm" />}
              Submit
            </Button>
          </div>
        </Form>
      </Container>
    </section>
  );
};

export default JoinUsForm;
